import type { ContactTier, PropertyContactWithDetails } from '@/client';
import type { ContactFormData } from '@/components/services/contact/ContactForm';

/** What the add composer starts from when it is opened from a saved contact rather than empty. */
export type ContactDraft = Partial<Omit<ContactFormData, 'email'>> & { tier?: ContactTier | null };

/** Opens the Contact tab of the add composer; a change of `key` repeats the request. */
export interface ContactAddRequest {
	key: number;
	categoryCode?: string;
	draft?: ContactDraft;
}

/**
 * The person's name, which a company-only card does not have. Such a card is filed under its
 * company's name, so a name that is only the company again is shown as no name at all.
 */
const getPersonName = (name: string | null | undefined, company: string | null | undefined) => {
	const trimmedName = name?.trim() ?? '';
	return trimmedName.toLowerCase() === (company?.trim().toLowerCase() ?? '') ? '' : trimmedName;
};

/** A saved contact's values as the form shows them: this property's corrections over the registry. */
export const getContactFormValues = (existingContact?: PropertyContactWithDetails): ContactFormData => {
	const company = existingContact?.override_company ?? existingContact?.contact.company ?? '';
	return {
		name: getPersonName(existingContact?.override_name ?? existingContact?.contact.name, company),
		company,
		email: existingContact?.override_email ?? existingContact?.contact.email ?? '',
		phone: existingContact?.override_phone ?? existingContact?.contact.phone ?? '',
		website: existingContact?.contact.website ?? '',
		address: existingContact?.contact.address ?? '',
		notes: existingContact?.notes ?? '',
	};
};

/**
 * A new card for the same person at another address: everything the saved one holds but its email,
 * which is who a card linked to an account is. The saved card keeps its access and its history.
 */
export const getContactDraft = (contact: PropertyContactWithDetails): ContactDraft => {
	const values = getContactFormValues(contact);
	return {
		name: values.name,
		company: values.company,
		phone: values.phone,
		website: values.website,
		address: values.address,
		notes: values.notes,
		tier: contact.tier ?? null,
	};
};
